import { Component, ViewChild, ElementRef, Output, EventEmitter, signal, input, effect } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { Observable } from "rxjs";
import { DateTime } from "luxon";
import { LegendPosition } from "@swimlane/ngx-charts";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "../../../environments/environment";
import { AbstractBuyerComponent } from "../abstract/buyer.component";
import { DatePipe, DecimalPipe } from "@angular/common";
import { CurrencyPipe } from "@angular/common";
import { Account } from "../../entity/Account";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { User } from "../../entity/User";
import { RateData } from "../../obj/RateData";
import { CurrentContractData } from "../../obj/CurrentContractData";
import { Contract } from "../../entity/Contract";
import { KilowantBarHomeAcceptOfferChartComponent } from "../chart/kilowant-charts-home-accept-offer.component";
import { Serie } from "../../obj/Serie";

@Component({
    selector: "kilowant-user-current-cost-data",
    templateUrl: "../../../html/incs/user-current-cost-data.html",
    imports: [RouterLink, KilowantBarHomeAcceptOfferChartComponent, DecimalPipe, CurrencyPipe, DatePipe]
})
export class UserCurrentCostDataComponent extends AbstractBuyerComponent {
    // Input signals
    client = input<User | null>(null);
    account = input.required<Account>();
    currentCost = input.required<CurrentContractData>();
    prefix = input<string>('');
    max_savings = input<string>('');
    currentPos = input<number>(0);
    totalNumber = input<number>(0);
    forceClose = input<Observable<void>>();
    forceOpen = input<Observable<void>>();
    autoOpenOnLoad = input<boolean>(false);

    @Output() switchUtilityAction = new EventEmitter<boolean>();

    @Output() openCloseAction = new EventEmitter<boolean>();

    @ViewChild('body') bodyElement!: ElementRef;

    // State signals
    viewPriceHistory = signal<boolean>(false);
    priceHistoryLoaded = signal<boolean>(false);

    // Open this panel by itself once BOTH facts hold: the parent asked for it, and
    // this panel's own history has arrived. `price-history-back` only exists behind
    // `@if (priceHistoryLoaded())` (user-current-cost-data.html:59), so opening any
    // earlier sets the flag against markup that is not in the DOM yet and the panel
    // snaps open with no animation when the data lands. Both halves are signals, so
    // whichever arrives second re-runs this — there is no order to get right, and no
    // delay to tune against a slow connection.
    private readonly openWhenHistoryLoaded = effect(() => {
        if (this.autoOpenOnLoad() && this.priceHistoryLoaded() && !this.viewPriceHistory()) {
            this.changeViewPriceHistory();
        }
    });
    view = signal<[number, number]>([400, 250]);
    legend = signal<boolean>(true);
    legendTitle = signal<string>('');
    legendPosition = signal<LegendPosition>(LegendPosition.Below);
    showLabels = signal<boolean>(true);
    animations = signal<boolean>(true);
    xAxis = signal<boolean>(true);
    yAxis = signal<boolean>(false);
    showYAxisLabel = signal<boolean>(false);
    showXAxisLabel = signal<boolean>(false);
    xAxisLabel = signal<string>('Year');
    yAxisLabel = signal<string>('');
    colorScheme = signal({ domain: ['#D0D0D0', '#A0A0A0'], name: 'kilowant', selectable: true, group: null! });

    rates = signal<RateData[]>([]);
    ratesPerPage = signal<number>(12);
    startRates = signal<number>(0);
    chartLineName = signal<string>('');

    paying_diff = signal<number | null>(null);
    diff_start = signal<string | null>(null);

    pagedRates = signal<RateData[]>([]);
    utilityAverageForHistory = signal<number>(0);
    communityAverageForHistory = signal<number>(0);
    historyContractVals = signal<number[]>([]);
    historyContractShiftsLeft = signal<number[]>([]);
    historyContractShiftsRight = signal<number[]>([]);
    historicalContracts: Contract[] = [];

    supplierLogoUrl = signal<string | null>(null);

    selectedDatasets = signal<number[]>([]);

    latestUtilityDate = signal<string>('');
    latestCommunityDate = signal<string>('');
    firstLoadPriceHistory = signal<boolean>(false);

    future_price = signal<number>(0);
    future_months = signal<number>(0);

    private forceCloseSub: any;
    private forceOpenSub: any;
    private timeoutId: any;

    constructor(
        router: Router,
        route: ActivatedRoute,
        dialog: MatDialog,
        protected datepipe: DatePipe,
        protected currencypipe: CurrencyPipe,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        super.ngOnInit();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.forceCloseSub) this.forceCloseSub.unsubscribe();
        if (this.forceOpenSub) this.forceOpenSub.unsubscribe();
        if (this.timeoutId) clearTimeout(this.timeoutId);
    }

    ngAfterContentInit(): void {
    }

    pageStart() {
        this.viewPriceHistory.set(false);
        this.loadSupplierLogo();
        this.selectedDatasets.set([1]);
        if (this.account().isCommunity) {
            this.selectedDatasets.update(arr => [...arr, 2]);
        }
        this.loadEstimatedDates();
        this.timeoutId = setTimeout(() => {
            this.firstLoadPriceHistory.set(true);
            this.loadPriceHistory();
        }, 1000);
        this.forceCloseSub = this.forceClose()?.subscribe(() => {
            if (this.viewPriceHistory()) {
                this.changeViewPriceHistory();
            }
        });
        this.forceOpenSub = this.forceOpen()?.subscribe(() => {
            if (!this.viewPriceHistory()) {
                this.changeViewPriceHistory();
            }
        });
    }

    loadPriceHistory(): void {
        if (this.bodyElement && this.account()) {
            this.view.set([this.bodyElement.nativeElement.offsetWidth - 30, 250]);
            let historyTermTo = null;
            if (this.currentCost().termTo != null) {
                historyTermTo = DateTime.fromJSDate(this.currentCost().termTo!).minus({ days: 1 }).toJSDate();
            }
            let historyTermFrom = this.currentCost().termFrom;

            if (this.currentCost().isRate && historyTermTo == null && this.currentCost().rateEnd != null) {
                historyTermTo = DateTime.fromJSDate(this.currentCost().rateEnd!).toJSDate();
            }

            if (this.currentCost().isRate && historyTermTo == null) {
                historyTermTo = this.currentDate.plus({ month: 11 }).toJSDate();
            }
            if (this.currentCost().isRate && historyTermFrom != null && DateTime.fromJSDate(historyTermFrom) <= this.currentDate.plus({ days: 1 })) {
                historyTermFrom = null;
            }
            let isCommunity = this.currentCost().compareToCommunity;
            var isUtility = !isCommunity;
            let noKilowantPrice = false;
            if (!this.currentCost().isRate && !this.currentCost().isKilowantContract && !isCommunity) {
                isUtility = true;
                noKilowantPrice = true;
            }
            this.generalService.getHistoricalRatesPerAccount(this.account(), historyTermFrom, historyTermTo, this.currentCost().isRate, !this.currentCost().isRate, false, noKilowantPrice, isUtility, isCommunity).then((data: any) => {
                this.rates.set(data);
                if (this.currentCost().isRate) {
                    this.estimateFutureSavings();
                } else {
                    this.estimateSavings();
                }
                const newStart = (this.rates().length - this.ratesPerPage());
                this.startRates.set(newStart < 0 ? 0 : newStart);
                this.chartLineName.set('Price Plan');
                this.priceHistoryLoaded.set(true);
                this.updateRates();
            });
        }
    }

    getPreviousDay(inDate: Date | null): Date | null {
        if (inDate != null) {
            return DateTime.fromJSDate(inDate).minus({ days: 1 }).toJSDate();
        }
        return inDate;
    }

    changeViewPriceHistory(): void {
        this.viewPriceHistory.update(v => !v);
        this.openCloseAction.next(this.viewPriceHistory());
    }

    estimateSavings(): void {
        this.paying_diff.set(0);
        this.diff_start.set(null);
        let total_avg_kilowant = 0;
        let total_avg_utility = 0;
        const ratesArray = this.rates();
        for (let i = 0; i < ratesArray.length; i++) {
            if (ratesArray[i].series.length > 1) {
                let kilowant_arr: number[] = [];
                let utility_arr: number[] = [];
                for (let x = 0; x < ratesArray[i].series.length; x++) {
                    if (ratesArray[i].series[x].name == 'Kilowant') {
                        if (this.diff_start() == null) {
                            this.diff_start.set(ratesArray[i].name);
                        }
                        kilowant_arr.push(ratesArray[i].series[x].value);
                    } else {
                        utility_arr.push(ratesArray[i].series[x].value);
                    }
                }
                if (this.estimateAvg(kilowant_arr) > 0 && this.estimateAvg(utility_arr) > 0) {
                    total_avg_kilowant += this.estimateAvg(kilowant_arr);
                    total_avg_utility += this.estimateAvg(utility_arr);
                }
            }
        }
        if (total_avg_utility > 0) {
            this.paying_diff.set(Math.round((total_avg_utility - total_avg_kilowant) / total_avg_utility * 100));
        }
    }

    historyLeft(): void {
        if (this.startRates() > 0) {
            this.startRates.update(val => {
                let newVal = val - this.ratesPerPage();
                return newVal < 0 ? 0 : newVal;
            });
            this.updateRates();
        }
    }

    historyRight(): void {
        if (this.startRates() < this.rates().length - this.ratesPerPage()) {
            this.startRates.update(val => {
                let newVal = val + this.ratesPerPage();
                let max = this.rates().length - this.ratesPerPage();
                return newVal > max ? max : newVal;
            });
            this.updateRates();
        }
    }

    updateRates(): void {
        const ratesArray = this.rates();
        const start = this.startRates();
        const perPage = this.ratesPerPage();
        const newPagedRates: RateData[] = [];
        let utilityAvg = 0;
        let communityAvg = 0;
        let i = start;
        let n = 0;
        while (i < ratesArray.length && n < perPage) {
            newPagedRates.push(ratesArray[i]);
            if (!this.currentCost().isRate) {
                newPagedRates[i].series.push(
                    new Serie("Price Plan " + (new CurrencyPipe("en-US")).transform(this.currentCost().price!, "USD", "symbol", "1.3-3") + "/kWh", this.currentCost().price!)
                );
            }
            i++;
            n++;
        }
        this.pagedRates.set(newPagedRates);

        if (!this.currentCost().isRate) {
            if (this.account()?.isCommunity) {
                communityAvg = this.currentCost().communityRate || 0;
            }
            utilityAvg = this.currentCost().utilityRate || 0;
        }
        this.utilityAverageForHistory.set(utilityAvg);
        this.communityAverageForHistory.set(communityAvg);

        const historyVals: number[] = [];
        const shiftsLeft: number[] = [];
        const shiftsRight: number[] = [];
        const ratesFrom = this.getDateFromRatesString(newPagedRates[0]?.name || '', false);
        const ratesTo = this.getDateFromRatesString(newPagedRates[newPagedRates.length - 1]?.name || '', true);

        if (this.currentCost().isRate) {
            if (this.historicalContracts) {
                for (let i = 0; i < this.historicalContracts.length; i++) {
                    const contractTermFrom = DateTime.fromJSDate(this.historicalContracts[i].termFrom);
                    const contractTermTo = DateTime.fromJSDate(this.historicalContracts[i].termTo).minus({ days: 1 });
                    if (contractTermFrom <= ratesTo && contractTermTo >= ratesFrom) {
                        historyVals.push(this.historicalContracts[i].getSalesPrice());
                        let leftShift = 0;
                        if (contractTermFrom > ratesFrom) {
                            leftShift = this.getDiffPeriodicityWise(contractTermFrom, ratesFrom, '');
                        }
                        shiftsLeft.push(leftShift);
                        let rightShift = 0;
                        if (contractTermTo < ratesTo) {
                            rightShift = this.getDiffPeriodicityWise(ratesTo, contractTermTo, '');
                        }
                        shiftsRight.push(rightShift);
                    }
                }
            }
        } else {
            const contractTermFrom = DateTime.fromJSDate(this.currentCost().termFrom!);
            const contractTermTo = DateTime.fromJSDate(this.currentCost().termTo!).minus({ days: 1 });
            if (contractTermFrom <= ratesTo && contractTermTo >= ratesFrom) {
                historyVals.push(this.currentCost().price!);
                shiftsLeft.push(0);
                shiftsRight.push(0);
            }
        }
        this.historyContractVals.set(historyVals);
        this.historyContractShiftsLeft.set(shiftsLeft);
        this.historyContractShiftsRight.set(shiftsRight);
    }

    loadSupplierLogo(): void {
        if (this.currentCost().isRate) {
            let url = environment.WEB_API_URL + "Utility/" + this.account().utility!.id + "/Logo";
            if (this.account()?.isCommunity) {
                url += "?community=true";
            }
            this.supplierLogoUrl.set(url);
        } else if (this.currentCost().supplierId != null) {
            this.supplierLogoUrl.set(environment.WEB_API_URL + "Company/" + this.currentCost().supplierId! + "/Logo");
        }
    }

    loadEstimatedDates(): void {
        this.generalService.getAccountGetLatestUtilityDate(this.account().id).then(x => {
            if (x.key != null) {
                this.latestUtilityDate.set(this.datepipe.transform(x.key, 'MMM. y')!);
            }
            if (x.value != null) {
                this.latestCommunityDate.set(this.datepipe.transform(x.value, 'MMM. y')!);
            }
        });
    }

    contractText(currentData: CurrentContractData): string {
        let outText = 'Previous Price Plan';
        if (this.isPeriodCurrent(currentData.termFrom, currentData.termTo)) {
            outText = 'Current Price Plan';
        }
        if (this.isPeriodFuture(currentData.termFrom, currentData.termTo)) {
            outText = 'Future Price Plan';
        }
        if (this.totalNumber() > 1) {
            outText += ' ' + this.currentPos() + ' of ' + this.totalNumber();
        }
        return outText;
    }

    isPeriodCurrent(termFrom: Date | null, termTo: Date | null): boolean {
        return ((termFrom == null || this.applySitesTimeZone(DateTime.fromJSDate(termFrom)).startOf('day') <= this.currentDate.startOf('day'))) && (termTo == null || (this.applySitesTimeZone(DateTime.fromJSDate(termTo)).startOf('day') >= this.currentDate.startOf('day')));
    }

    isPeriodFuture(termFrom: Date | null, termTo: Date | null): boolean {
        return (termFrom != null && this.applySitesTimeZone(DateTime.fromJSDate(termFrom)).startOf('day') > this.currentDate.startOf('day'));
    }

    getDiffPeriodicityWise(fromDate: DateTime, toDate: DateTime, periodicity: string): number {
        if (periodicity == null || periodicity == '' || periodicity == 'monthly') {
            return Math.floor(fromDate.diff(toDate, ['months'])['months']);
        }
        if (periodicity == 'quarterly') {
            return Math.floor(fromDate.diff(toDate, ['months'])['months'] / 3);
        }
        if (periodicity == 'yearly') {
            return Math.floor(fromDate.diff(toDate, ['years'])['years']);
        }
        return 0;
    }

    estimateFutureSavings(): void {
        this.paying_diff.set(0);
        this.diff_start.set(null);
        let total_avg_kilowant = 0;
        let total_avg_utility = 0;
        let futurePrice = 0;
        let futureMonths = 0;
        const ratesArray = this.rates();
        for (let i = 0; i < ratesArray.length; i++) {
            const arr = ratesArray[i].name.split('/');
            const month = parseInt(arr[0]);
            const year = parseInt(arr[1]);
            if (DateTime.local(year, month, 1) > this.currentDate) {
                futureMonths += 1;
                for (let x = 0; x < ratesArray[i].series.length; x++) {
                    if (ratesArray[i].series[x].name != 'Kilowant') {
                        futurePrice += ratesArray[i].series[x].value;
                    }
                }
            }

            if (ratesArray[i].series.length > 1) {
                let kilowant_arr: number[] = [];
                let utility_arr: number[] = [];
                for (let x = 0; x < ratesArray[i].series.length; x++) {
                    if (DateTime.local(year, month, 1) < this.currentDate) {
                        if (ratesArray[i].series[x].name == 'Kilowant') {
                            if (this.diff_start() == null) {
                                this.diff_start.set(DateTime.local(year, month, 1).monthShort + ' ' + year);
                            }
                            kilowant_arr.push(ratesArray[i].series[x].value);
                        } else {
                            utility_arr.push(ratesArray[i].series[x].value);
                        }
                    }
                }
                if (this.estimateAvg(kilowant_arr) > 0 && this.estimateAvg(utility_arr) > 0) {
                    total_avg_kilowant += this.estimateAvg(kilowant_arr);
                    total_avg_utility += this.estimateAvg(utility_arr);
                }
            }
        }
        if (futureMonths > 0) {
            futurePrice = futurePrice / futureMonths;
        }
        this.future_price.set(futurePrice);
        this.future_months.set(futureMonths);
        if (total_avg_utility > 0) {
            this.paying_diff.set(Math.round((total_avg_utility - total_avg_kilowant) / total_avg_utility * 100));
        }
    }

    getColorBasedOnUtility(): string {
        if (this.account() == null) {
            return '';
        }
        if (this.account().isCommunity) {
            return environment.RI_COMMUNITY_COLOR;
        }
        return environment.RI_UTILITY_COLOR;
    }

    changeCurrentElectricityPlan(): void {
        this.switchUtilityAction.next(true);
    }

    isOnUtilityOnly(): boolean {
        if (this.totalNumber() == 1 && this.currentCost().isRate && this.currentCost().rateEnd != null) {
            return true;
        }
        return false;
    }
}